import CallSplitIcon from '@mui/icons-material/CallSplit';
import CodeIcon from '@mui/icons-material/Code';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Switch from '@mui/material/Switch';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MetaCampaignInfoComponent from 'components/common/MetaCampaignInfo';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import type React from 'react';
import { useState } from 'react';
import type { BusinessUnit } from 'services/types/businessUnit';
import type { ExtendedLead } from 'services/types/lead';
import type { User } from 'services/types/user';
import { CYAN, NAVY, RADIUS_CARD, RADIUS_ITEM, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import getAdvancePercentage from 'utils/getAdvancePercentage';
import { findPartnerBySource } from 'utils/partnerCampaigns';

import InfoItem from './InfoItem';
import JsonViewerDialog, { type JsonPayloadEntry } from './JsonViewerDialog';

interface LeadsSectionProps {
  businessUnit?: BusinessUnit;
  clientLeads?: ExtendedLead[];
  currentUser?: User | null;
}

/**
 * Translates lead status from English to Spanish
 * @param status - The lead status in English
 * @returns The translated status in Spanish
 */
const translateLeadStatus = (status: string): string => {
  const statusTranslations: Record<string, string> = {
    CREATED: 'Creado',
    HANDLED_BY_AI: 'IA',
    ASSIGNED_TO_SALESMAN: 'Asignado',
    PENDING_TO_BE_HANDLED: 'Asignado',
    PENDING_TO_BE_ASSIGNED: 'Por asignar',
    HANDLED_BY_SALESMAN: 'Atendido',
    CLOSED: 'Cerrado',
  };

  return statusTranslations[status] || status;
};

/**
 * Helper function to create InfoItem components
 * @param label - The label for the info item
 * @param value - The value to display
 * @param fullWidth - Whether to take full width
 * @returns InfoItem component
 */
const createInfoItem = (label: string, value: React.ReactNode, fullWidth = false) => (
  <InfoItem key={`${label}-${Math.random()}`} label={label} value={value} fullWidth={fullWidth} />
);

/**
 * Formats a source URL for better display
 * @param source - The source string (could be a URL or simple text)
 * @returns Formatted source for display
 */
const formatSourceForDisplay = (source: string): string => {
  if (!source) return '';

  // If it's a URL, extract the domain only for better mobile display
  if (source.startsWith('http') || source.startsWith('@whatsapp-http')) {
    try {
      // Remove @whatsapp- prefix if present
      const cleanUrl = source.replace(/^@whatsapp-/, '');
      const url = new URL(cleanUrl);

      // For mobile, just show domain + "..." to keep it short
      return `${url.hostname}...`;
    } catch {
      // If URL parsing fails, truncate aggressively
      return source.length > 20 ? `${source.substring(0, 20)}...` : source;
    }
  }

  // For non-URLs, truncate more aggressively for mobile
  return source.length > 20 ? `${source.substring(0, 20)}...` : source;
};

/**
 * Comprehensive leads section component with filtering and detailed lead information
 * @param client - Client data
 * @param businessUnit - Business unit information
 * @param currentUser - Current user data
 * @param clientLeads - Array of client leads
 * @returns React component displaying leads information
 */
// Both leads of a hand-off carry the same `handoff` object, so the lead's own business unit is
// what says whether it is the one that let the client go or the one that received them.
const describeHandoff = (lead: ExtendedLead, nameOf: (businessUnitId: number) => string): string | null => {
  const handoff = lead.customAttributes?.handoff;
  if (!handoff) return null;
  const isOrigin = lead.businessUnitId === handoff.fromBusinessUnitId;
  const counterpart = isOrigin ? handoff.toBusinessUnitId : handoff.fromBusinessUnitId;
  return `${isOrigin ? 'Derivado a' : 'Derivado desde'} ${nameOf(counterpart)} — ${handoff.reason}`;
};

const LeadsSection: React.FC<LeadsSectionProps> = ({
  businessUnit = undefined,
  clientLeads = undefined,
  currentUser = null,
}) => {
  const { businessUnits } = useBusinessUnits();
  const businessUnitNameOf = (businessUnitId: number): string =>
    businessUnits?.find((unit) => unit.id === businessUnitId)?.name ?? `unidad ${businessUnitId}`;
  const [showDiscarded, setShowDiscarded] = useState(false);
  const [expandedAccordions, setExpandedAccordions] = useState<Record<number, boolean>>({});
  const [jsonDialog, setJsonDialog] = useState<{
    open: boolean;
    title: string;
    payloads: JsonPayloadEntry[];
  }>({ open: false, title: '', payloads: [] });

  if (!clientLeads || clientLeads.length === 0) {
    return (
      <Typography
        sx={{
          color: alpha(NAVY, 0.5),
          fontStyle: 'italic',
        }}
      >
        No hay leads disponibles para este cliente
      </Typography>
    );
  }

  const businessUnitType = businessUnit?.type || 'default';

  // Filter leads based on showDiscarded toggle
  const filteredLeads = showDiscarded ? clientLeads : clientLeads.filter((lead) => !lead.discarded);

  const discardedCount = clientLeads.filter((lead) => lead.discarded).length;
  const activeCount = clientLeads.filter((lead) => !lead.discarded).length;

  /**
   * Renders individual lead details based on business unit type
   * @param lead - Lead data to render
   * @returns React component with lead details
   */
  const renderLeadDetails = (lead: ExtendedLead) => (
    <Box>
      <Grid container spacing={1} sx={{ mb: 2 }}>
        {/* Common fields for all business unit types */}
        {createInfoItem('ID Lead', lead.id)}
        {lead.externalId && createInfoItem('ID Externo', lead.externalId)}
        {createInfoItem('Estado', translateLeadStatus(lead.status))}
        {createInfoItem('Origen', lead.source)}
        {lead.origin && createInfoItem('Categoría', lead.origin)}
        {lead.subOrigin && createInfoItem('Suborigen', lead.subOrigin)}
        {findPartnerBySource(lead.source) && createInfoItem('Alianza', findPartnerBySource(lead.source)?.label)}
        {lead.comment && createInfoItem('Propuesta de valor', lead.comment, true)}
        {lead.initialMessage && createInfoItem('Mensaje inicial', lead.initialMessage, true)}

        {/* Car dealership specific fields */}
        {businessUnitType === 'car_dealership' && (
          <>
            {createInfoItem('Marca de interés', lead.initialBrandOfInterest)}
            {createInfoItem('Modelo de interés', lead.initialModelOfInterest)}
            {lead.dealership && createInfoItem('Concesionario', lead.dealership)}
            {lead.branchOffice && createInfoItem('Sucursal', lead.branchOffice)}
            {lead.externalLeadOwner && createInfoItem('Propietario externo', lead.externalLeadOwner)}
          </>
        )}

        {/* Financial institution specific fields */}
        {businessUnitType === 'financial_institution' && (
          <>
            {createInfoItem('En Dicom', lead.client?.customAttributes?.isInDicom ? 'Sí' : 'No')}
            {lead.client?.customAttributes?.income &&
              createInfoItem('Ingresos', `$${lead.client.customAttributes.income.toLocaleString('es-CL')}`)}
            {lead.client?.customAttributes?.debtsMonthlyPayment &&
              createInfoItem(
                'Pago mensual deudas',
                `$${lead.client.customAttributes.debtsMonthlyPayment.toLocaleString('es-CL')}`
              )}
            {lead.client?.customAttributes?.financialLoadRange &&
              createInfoItem(
                'Carga financiera',
                (() => {
                  const range = lead.client.customAttributes.financialLoadRange;
                  if (range === 'low') return 'Baja';
                  if (range === 'medium') return 'Media';
                  if (range === 'high') return 'Alta';
                  return range;
                })()
              )}
          </>
        )}

        {/* Insurance broker specific fields */}
        {businessUnitType === 'insurance_broker' &&
          (() => {
            const loanProtectionPercentage = getAdvancePercentage(lead, 'loanProtectionInsurance');
            const unemploymentPercentage = getAdvancePercentage(lead, 'unemploymentInsurance');
            return (
              <>
                {loanProtectionPercentage !== undefined &&
                  createInfoItem('Porcentaje adelanto seguro de desgravamen', `${loanProtectionPercentage}%`)}
                {unemploymentPercentage !== undefined &&
                  createInfoItem('Porcentaje adelanto seguro de desempleo', `${unemploymentPercentage}%`)}
              </>
            );
          })()}

        {/* Campaign and tracking info */}
        {lead.outboundCampaignId && createInfoItem('Campaña', lead.outboundCampaignId)}
        {lead.UTM?.source && createInfoItem('UTM Source', lead.UTM.source)}
        {lead.UTM?.medium && createInfoItem('UTM Medium', lead.UTM.medium)}
        {lead.UTM?.campaign && createInfoItem('UTM Campaign', lead.UTM.campaign)}
      </Grid>

      {/* Campaign details */}
      {lead.businessUnitAd && (
        <>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              mb: 1,
              color: 'text.secondary',
            }}
          >
            📊 Información de Campaña
          </Typography>
          <Grid container spacing={1} sx={{ mb: 2, ml: 1 }}>
            {createInfoItem('ID de campaña', lead.businessUnitAd.adSourceId)}
            {createInfoItem('Nombre de campaña', lead.businessUnitAd.campaignSourceName)}
            {lead.businessUnitAd.campaignExternalCRMId &&
              createInfoItem('ID externo CRM', lead.businessUnitAd.campaignExternalCRMId)}
          </Grid>
        </>
      )}

      {/* Additional information */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        {/* Message counts - only visible for admin users */}
        {currentUser?.isAdmin && (
          <>
            {createInfoItem('Mensajes totales', lead.messagesCount)}
            {createInfoItem('Mensajes del cliente', lead.clientMessagesCount)}
            {createInfoItem('Mensajes fallidos', lead.failedMessagesCount)}
            {createInfoItem('Plantillas enviadas', lead.templatesSentCount)}
          </>
        )}
        {lead.AIComment && createInfoItem('Comentario AI', lead.AIComment, true)}
        {lead.endReason && createInfoItem('Motivo de término', lead.endReason, true)}
        {lead.visitDate && createInfoItem('Fecha de visita', new Date(lead.visitDate).toLocaleString('es-CL'))}
        {createInfoItem('Descartado', lead.discarded ? 'Sí' : 'No')}
        {createInfoItem('Subido a CRM', lead.CRMUpdateAt ? new Date(lead.CRMUpdateAt).toLocaleString('es-CL') : '-')}
        {createInfoItem('Duplicado CRM', lead.isCrmDuplicate ? 'Sí' : 'No')}
      </Grid>

      {/* URL if available */}
      {lead.url && (
        <Box sx={{ mt: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: alpha(NAVY, 0.5),
              fontWeight: 500,
            }}
          >
            URL:
          </Typography>
          <Typography
            variant="body2"
            component="a"
            href={lead.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              ml: 1,
              color: NAVY,
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
              wordBreak: 'break-all',
            }}
          >
            {lead.url}
          </Typography>
        </Box>
      )}

      <MetaCampaignInfoComponent
        leadId={lead.id}
        metaAdId={lead.metaAdId ?? null}
        isExpanded={expandedAccordions[lead.id] || false}
      />

      {/* CRM data viewer button */}
      {(lead.webhookBodyAtCreation || lead.CRMCreationPayload || lead.CRMCreationResponse) && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="text"
            size="small"
            startIcon={<CodeIcon sx={{ fontSize: 14 }} />}
            fullWidth
            onClick={() => {
              setJsonDialog({
                open: true,
                title: `Datos de Integración — Lead #${lead.id}`,
                payloads: [
                  {
                    label: 'Payload de Creación',
                    data: lead.webhookBodyAtCreation,
                    filename: `lead_${lead.id}_webhook_creation.json`,
                  },
                  {
                    label: 'Payload del CRM',
                    data: lead.CRMCreationPayload,
                    filename: `lead_${lead.id}_crm_payload.json`,
                  },
                  {
                    label: 'Respuesta del CRM',
                    data: lead.CRMCreationResponse,
                    filename: `lead_${lead.id}_crm_response.json`,
                  },
                ],
              });
            }}
            sx={{
              textTransform: 'none',
              justifyContent: 'flex-start',
              borderRadius: RADIUS_ITEM,
              color: NAVY,
              backgroundColor: SURFACE_CONTAINER_LOWEST,
              fontSize: '0.75rem',
              py: 0.75,
              '&:hover': { backgroundColor: alpha(NAVY, 0.06) },
            }}
          >
            Datos de Integración
          </Button>
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
      {/* Summary and toggle section */}
      <Box sx={{ m: 1 }}>
        <Grid
          container
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Grid>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              <strong>Total:</strong> {clientLeads.length} leads
              {activeCount > 0 && (
                <span style={{ marginLeft: 8 }}>
                  (<strong>{activeCount}</strong> activos)
                </span>
              )}
              {discardedCount > 0 && (
                <span style={{ marginLeft: 8 }}>
                  (<strong>{discardedCount}</strong> descartados)
                </span>
              )}
            </Typography>
          </Grid>
          <Grid>
            <FormControlLabel
              control={
                <Switch
                  checked={showDiscarded}
                  onChange={(e) => setShowDiscarded(e.target.checked)}
                  size="small"
                  disabled={discardedCount === 0}
                  color={showDiscarded ? 'error' : 'primary'}
                />
              }
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {showDiscarded ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                  <Typography variant="caption" color={discardedCount === 0 ? 'text.disabled' : 'text.primary'}>
                    {showDiscarded ? 'Ocultar descartados' : 'Mostrar descartados'}
                    {discardedCount === 0 && ' (0)'}
                  </Typography>
                </Box>
              }
              sx={{ margin: 0 }}
            />
          </Grid>
        </Grid>
      </Box>
      {/* No leads message when filtered */}
      {filteredLeads.length === 0 ? (
        <Typography
          sx={{
            color: 'text.secondary',
            fontStyle: 'italic',
          }}
        >
          No hay leads {showDiscarded ? 'descartados' : 'activos'} para este cliente
        </Typography>
      ) : (
        /* Individual lead accordions */
        <Box>
          {filteredLeads.map((lead) => (
            <Accordion
              key={lead.id}
              expanded={expandedAccordions[lead.id] || false}
              onChange={(_, isExpanded) => {
                setExpandedAccordions((prev) => ({
                  ...prev,
                  [lead.id]: isExpanded,
                }));
              }}
              sx={{
                mb: 0.5,
                boxShadow: '0px 1px 4px rgba(21, 24, 58, 0.08)',
                borderRadius: `${RADIUS_CARD} !important`,
                backgroundColor: lead.discarded ? 'rgba(253, 237, 237, 0.92)' : SURFACE_CONTAINER_LOWEST,
                '&:before': { display: 'none' },
                '&.Mui-expanded': {
                  margin: '0 0 4px 0',
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: alpha(NAVY, 0.55) }} />}
                sx={{
                  backgroundColor: lead.discarded ? 'rgba(253, 237, 237, 0.92)' : SURFACE_CONTAINER_LOWEST,
                  borderRadius: `${RADIUS_CARD} ${RADIUS_CARD} 0 0`,
                  minHeight: 48,
                  overflow: 'hidden',
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    gap: 1,
                    overflow: 'hidden',
                    minWidth: 0,
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <Tooltip title={lead.source || `Lead #${lead.id}`} arrow placement="top">
                    <Box
                      sx={{
                        minWidth: 0,
                        flex: '1 1 0',
                        maxWidth: { xs: '120px', sm: '150px', md: '200px' },
                        overflow: 'hidden',
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                          width: '100%',
                          display: 'block',
                        }}
                      >
                        {lead.source ? formatSourceForDisplay(lead.source) : `Lead #${lead.id}`}
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1, minWidth: 'fit-content' }}>
                    {lead.businessUnitId && (
                      <Tooltip
                        title={describeHandoff(lead, businessUnitNameOf) ?? 'Unidad de negocio del lead'}
                        placement="top"
                        arrow
                      >
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1,
                            py: 0.25,
                            borderRadius: RADIUS_ITEM,
                            backgroundColor: alpha(CYAN, lead.customAttributes?.handoff ? 0.16 : 0.08),
                            color: NAVY,
                            fontSize: '0.7rem',
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {lead.customAttributes?.handoff && <CallSplitIcon sx={{ fontSize: 13 }} />}
                          {businessUnitNameOf(lead.businessUnitId)}
                        </Box>
                      </Tooltip>
                    )}
                    <Chip
                      label={translateLeadStatus(lead.status)}
                      size="small"
                      color={lead.discarded ? 'error' : 'primary'}
                      variant={lead.discarded ? 'outlined' : 'filled'}
                    />
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {new Date(lead.createdAt).toLocaleDateString('es-CL', {
                          day: '2-digit',
                          month: '2-digit',
                          year: '2-digit',
                        })}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                          fontSize: '0.7rem',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {new Date(lead.createdAt).toLocaleTimeString('es-CL', {
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: false,
                        })}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ py: 1 }}>{renderLeadDetails(lead)}</AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
      <JsonViewerDialog
        open={jsonDialog.open}
        onClose={() => setJsonDialog((prev) => ({ ...prev, open: false }))}
        title={jsonDialog.title}
        payloads={jsonDialog.payloads}
      />
    </Box>
  );
};

export default LeadsSection;
